import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Container, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import useSWRMutation from "swr/mutation";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import LinkIcon from "@mui/icons-material/Link";

import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { useFetchDetail } from "@hooks/useFetchDetail";
import {
  ServicesURL,
  downloadBlobFile,
  formatSessionOccupiedError,
  onErrorMessage,
} from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ButtonBackPage from "@components/ButtonBackPage";
import { ActionsType, ColumnsType } from "src/interfaces/Common";
import { axiosInstance, fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { translateHeaders } from "@utils/utilsTranslateHeaders";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { useFetchTable } from "@hooks/useFetchDataTable";
import AnimationFadeIn from "@components/AnimationFadeIn";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import { useErrorContext } from "@context/ErrorContext";
import dayjs from "dayjs";
import DownloadIcon from "@mui/icons-material/Download";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { useAuth } from "@context/auth";
import { RolesDigitedEnum } from "@context/auth/types";
import { buildDateStr } from "@utils/utilsDate";
import { useFilters } from "@context/filters";
import FormLoadingStateCst from "@components/FormLoadingStateCst";
import AsyncProcessBanner from "@components/AsyncProcessBanner";
import { useAsyncProcess } from "@hooks/useAsyncProcess";
import {
  AsyncProcessStatusEnum,
  AsyncProcessTypeEnum,
  AsyncProcessDetailResponse,
} from "src/interfaces/AsyncProcess";
import {
  getRefreshOutcome,
  getRefreshResultLines,
  getRefreshResultTitleKey,
  RefreshOutcome,
} from "@utils/refreshCertificatesOutcome";
import {
  ModalEditionChange,
  EditionWithSessions,
} from "@components/ModalEditionChange";
import { Warning } from "@mui/icons-material";

const SubscribedUsers: React.FC<{
  page: PagesID;
  showIssuedCertificates?: boolean;
}> = ({ page, showIssuedCertificates }) => {
  const { t, i18n } = useTranslation([
    "customers-page",
    "toast-message",
    "modal",
  ]);
  const {
    id = "1",
    idLO,
    idEdition,
    idEditionLOActivity,
    activityType,
    idCourse,
    idPath,
  } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${idEdition ? PagesID["enrollment.subscribed.edition"] : page}`;
  const { setErrorContext, setOpenErrorContext } = useErrorContext();

  const router = useLocation();
  const navigate = useNavigate();

  const [openToastDeleted, setOpenToastDeleted] = useState<{
    s: StatusEnum;
    message: string;
  }>();
  const [enrollDeleted, setEnrollDeleted] = useState<any>();
  const [enrollGroupDeleted, setEnrollGroupDeleted] = useState<any>();
  const [enrollSingleEdit, setEnrollSingleEdit] = useState<any>();
  const [enrollGroupEdit, setEnrollGroupEdit] = useState<any>();
  const location = useLocation();
  const [groupIDWarning, setGroupIDWarning] = useState<any>();
  const [editDescriptionErrorModal, setEditDescriptionErrorModal] =
    useState(false);
  const [titleError, setTitleError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  const [certificateDataToRefresh, setCertificateDataToRefresh] =
    useState<any>();
  const [
    openModalRefreshCertificateMassive,
    setOpenModalRefreshCertificateMassive,
  ] = useState(false);
  const [openToastRefreshCertificate, setOpenToastRefreshCertificate] =
    useState<{
      s: StatusEnum;
      message: string;
    }>();

  const [openModalEditionChange, setOpenModalEditionChange] = useState(false);
  const [openModalEditionChangeToast, setOpenModalEditionChangeToast] =
    useState<{
      s: StatusEnum;
      message: string;
      customContent?: JSX.Element;
    }>();
  const [selectedEditionToReschedule, setSelectedEditionToReschedule] =
    useState<any>(null);
  const [enrollableEditionsWithSessions, setEnrollableEditionsWithSessions] =
    useState<EditionWithSessions[]>([]);
  const [isPollingEditionChange, setIsPollingEditionChange] = useState(false);
  const [newEditionIdForPolling, setNewEditionIdForPolling] = useState<
    number | null
  >(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingCompletedRef = useRef<boolean>(false);

  const [inProgressWarning, setInProgressWarning] = useState<{
    type: "delete" | "reschedule";
    row: any;
  } | null>(null);

  const isOverallCatalog = useMemo(() => {
    return router.pathname.includes("overall-catalog");
  }, [router.pathname]);

  const isVirtualEditionSubscribedPage = useMemo(() => {
    return (
      page === PagesID["corporate.virtual-editions.subscribed"] ||
      router.pathname.includes("virtual-editions")
    );
  }, [page, router.pathname]);

  const getURLBasedOnParams = () => {
    if (idEdition) {
      return `${ServicesURL.getEnrollEditionDetail}?corporateId=${id}${`&editionId=${idEdition}`}${location?.state?.setGroup && location?.state?.row ? `&massiveGroupId=${location?.state?.row[location?.state?.setGroup]}` : ``}${showIssuedCertificates ? "&showIssuedCertificate=true" : ""}`;
    } else {
      if (activityType) {
        if (isOverallCatalog) {
          return `${ServicesURL.getEnrollment}?corporateId=${id}${`&objectId=${idLO}`}&onlyStandAlone=true${location?.state?.setGroup && location?.state?.row ? `&massiveGroupId=${location?.state?.row[location?.state?.setGroup]}` : ``}${showIssuedCertificates ? "&showIssuedCertificate=true" : ""}`;
        } else {
          return `${ServicesURL.getEnrollment}?corporateId=${id}${`&objectId=${idPath || idCourse || idLO}`}&onlyStandAlone=true${location?.state?.setGroup && location?.state?.row ? `&massiveGroupId=${location?.state?.row[location?.state?.setGroup]}` : ``}${showIssuedCertificates ? "&showIssuedCertificate=true" : ""}`;
        }
      } else {
        return `${ServicesURL.getEnrollment}?corporateId=${id}${idLO ? `&objectId=${idLO}` : ``}&onlyStandAlone=true${location?.state?.setGroup && location?.state?.row ? `&massiveGroupId=${location?.state?.row[location?.state?.setGroup]}` : ``}${showIssuedCertificates ? "&showIssuedCertificate=true" : ""}`;
      }
    }
  };

  const { dataTable, columns, actionForRow, actionsTopToolbar, mutate } =
    useFetchTable(
      getURLBasedOnParams(),
      idEdition ? PagesID["enrollment.subscribed.edition"] : page,
      filters[filterId],
    );

  // se nessuna riga ha un certificato disabilito il tasto download massivo
  const certificatesAvailable = useMemo(() => {
    const rows = dataTable?.enrollmentResponses ?? [];

    return rows.some(
      (el: any) =>
        (Array.isArray(el.certificateIds) && el.certificateIds.length > 0) ||
        !!el.certificateId,
    );
  }, [dataTable]);

  const {
    currentProcess: currentRefreshProcess,
    progressPercentage: refreshProgressPercentage,
    trackStartedProcess: trackRefreshProcess,
    downloadDiscardsCsv: downloadRefreshDiscardsCsv,
    dismissProcess: dismissRefreshProcess,
  } = useAsyncProcess({
    processType: AsyncProcessTypeEnum.CERTIFICATE_REFRESH,
    corporateId: id,
    onCompleted: (process) => {
      loadRefreshOutcome(process.processUuid);
      mutate && mutate();
    },
  });

  const [refreshOutcome, setRefreshOutcome] = useState<RefreshOutcome | null>(
    null,
  );

  const loadRefreshOutcome = React.useCallback(async (processUuid: string) => {
    try {
      const { data } = await axiosInstance.get<AsyncProcessDetailResponse>(
        ServicesURL.getAsyncProcessDetail.replace(":processUuid", processUuid),
      );

      const discards = data?.discards ?? [];
      const signed = discards.filter((d) => d.errorCode === "SIGNED").length;
      const noPlaceholder = discards.filter(
        (d) => d.errorCode === "NO_PLACEHOLDER_IN_SCOPE",
      ).length;

      const handled = data?.progress?.totalRecords ?? 0;
      const updated = data?.progress?.processedRecords ?? 0;
      const alreadyUpdated = Math.max(
        handled - updated - signed - noPlaceholder,
        0,
      );

      setRefreshOutcome(
        getRefreshOutcome({
          refreshed: [
            ...Array(updated).fill({ updatedPlaceholders: 1 }),
            ...Array(alreadyUpdated).fill({ updatedPlaceholders: 0 }),
          ],
          skipped: [
            ...Array(signed).fill({ reason: "SIGNED" }),
            ...Array(noPlaceholder).fill({ reason: "NO_PLACEHOLDER_IN_SCOPE" }),
          ],
        }),
      );
    } catch (error) {
      console.error(error);
      setRefreshOutcome(null);
    }
  }, []);

  const { dataDetail } = useFetchDetail(
    ServicesURL.corporate,
    id || "",
    PagesID["corporate"],
  );

  const { trigger: triggerDelete } = useSWRMutation(
    `${ServicesURL.deleteEnrollment}`,
    (url: string, { arg }: { arg: { enrollmentEId: string } }) =>
      fetcher(`${url}?enrollmentEId=${arg.enrollmentEId}`, MethodType.DELETE),
  );

  const { trigger: triggerRefreshCertificateData } = useSWRMutation(
    `${ServicesURL.refreshIssuedCertificatePlaceholders}`,
    (
      url: string,
      {
        arg,
      }: {
        arg: {
          corporateId: number;
          objectId: number;
          learnerId: number;
          issuedCertificateIds: number[];
        };
      },
    ) => fetcher(url, MethodType.POST, arg),
  );

  const { trigger: triggerRefreshCertificateDataMassive } = useSWRMutation(
    `${ServicesURL.refreshIssuedCertificatePlaceholdersMassive}`,
    (
      url: string,
      { arg }: { arg: { corporateId: number; objectId: number } },
    ) => fetcher(url, MethodType.POST, arg),
  );

  const { trigger: triggerDownloadEnrolled } = useSWRMutation(
    `${ServicesURL.downloadEnrolledEdition}`,
    (url: string, { arg }: { arg: string }) =>
      fetcher(`${url}${arg}`, MethodType.GET),
  );

  const { trigger: triggerDeleteEdition } = useSWRMutation(
    `${ServicesURL.getEnrollEdition}`,
    (url: string, { arg }: { arg: { studentEid: string; objectId: string } }) =>
      fetcher(
        `${url}?corporateId=${id}&objectId=${arg.objectId}&studentEid=${arg.studentEid}&editionId=${idEdition}`,
        MethodType.DELETE,
      ),
  );

  const { trigger: triggerDeleteGroups } = useSWRMutation(
    `${ServicesURL.postEnrollmentMassive}`,
    (url: string, { arg }: { arg: { groupId: any; groupType: any } }) =>
      fetcher(
        `${url}?corporateId=${id}&groupId=${arg.groupId}&groupType=${arg.groupType}&objectId=${idLO}`,
        MethodType.DELETE,
      ),
  );

  const { trigger: triggerGetEnrollableEditions } = useSWRMutation(
    ServicesURL.getEnrollableEditions,
    (
      url: string,
      {
        arg,
      }: { arg: { corporateId: string; objectId: string; studentEid: string } },
    ) =>
      fetcher(
        `${url}?corporateId=${arg.corporateId}&objectId=${arg.objectId}&userId=${arg.studentEid}`,
        MethodType.GET,
      ),
    { onError: () => {} },
  );

  const { trigger: triggerGetEnrollmentStatus } = useSWRMutation(
    `${ServicesURL.getEnrollmentStatus}`,
    (
      url: string,
      {
        arg,
      }: { arg: { corporateId: string; editionId: string; userId: string } },
    ) =>
      fetcher(
        `${url}?corporateId=${arg.corporateId}&editionId=${arg.editionId}&userId=${arg.userId}`,
        MethodType.GET,
      ),
    { onError: () => {} },
  );

  const {
    trigger: triggerPatchEnrollment,
    isMutating: isMutatingPatchEnrollment,
  } = useSWRMutation(
    ServicesURL.patchEnrollment,
    (url: string, { arg }: { arg: any[] }) =>
      fetcher(url, MethodType.PATCH, arg),
    { onError: () => {} },
  );

  const pollEnrollmentStatus = useCallback(async () => {
    if (!selectedEditionToReschedule || !newEditionIdForPolling) return;

    try {
      const response: any = await triggerGetEnrollmentStatus({
        corporateId: String(id),
        editionId: String(newEditionIdForPolling),
        userId: String(selectedEditionToReschedule.idUser),
      });

      const currentStatus = response?.status || response?.data?.status;
      const currentErrorMessage =
        response?.errorMessage || response?.data?.errorMessage || "";

      if (pollingCompletedRef.current) return;

      if (currentStatus === "COMPLETE") {
        pollingCompletedRef.current = true;
        setIsPollingEditionChange(false);
        setOpenModalEditionChangeToast({
          s: StatusEnum.OK,
          message: t("change-edition.edition_change_success", { ns: "modal" }),
        });
        mutate();
      } else if (currentStatus === "ERROR") {
        pollingCompletedRef.current = true;
        setIsPollingEditionChange(false);
        if (currentErrorMessage.includes("MAX_EDITION_CAPACITY_REACHED")) {
          setOpenModalEditionChangeToast({
            s: StatusEnum.KO,
            message: t("max-capacity-reached-error", { ns: "modal" }),
          });
        } else {
          setOpenModalEditionChangeToast({
            s: StatusEnum.KO,
            message: t("change-edition.enrollment_edition_error", {
              ns: "modal",
            }),
          });
        }
        mutate();
      }
    } catch (error) {
      console.error("Polling error:", error);
    }
  }, [
    id,
    newEditionIdForPolling,
    selectedEditionToReschedule,
    triggerGetEnrollmentStatus,
    mutate,
    t,
  ]);

  useEffect(() => {
    if (!isPollingEditionChange) {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      return;
    }

    const startTime = Date.now();
    const MAX_POLLING_DURATION = 120000; // 2 minutes
    const MAX_INTERVAL = 20000; // 20 seconds
    let currentInterval = 5000; // Start with 5 seconds

    const scheduleNextPoll = () => {
      const elapsed = Date.now() - startTime;

      if (elapsed >= MAX_POLLING_DURATION) {
        pollingCompletedRef.current = true;
        setIsPollingEditionChange(false);
        setOpenModalEditionChangeToast({
          s: StatusEnum.KO,
          message: t("change-edition.enrollment_polling_error", {
            ns: "modal",
          }),
        });
        mutate();
        return;
      }

      pollEnrollmentStatus();

      if (currentInterval < MAX_INTERVAL) {
        currentInterval += 5000;
      }

      pollingTimeoutRef.current = setTimeout(scheduleNextPoll, currentInterval);
    };

    pollingTimeoutRef.current = setTimeout(scheduleNextPoll, currentInterval);

    return () => {
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    };
  }, [isPollingEditionChange, pollEnrollmentStatus, mutate, t]);

  //:TODO  Da eliminare
  /*
  useEffect(() => {
    if (location?.state?.row && location?.state?.setGroup) {
      setFilters &&
        setFilters(`${user?.id_user}_${page}`, {
          massiveGroupId: location?.state?.row[location?.state?.setGroup],
        });
    }
  }, []);
  */

  // La gerarchia da inviare è quella dell'iscrizione selezionata, non quella della rotta di provenienza
  const getHierarchyIds = (row: any) => {
    const { parentId, grandParentId } = row?.learningObject || {};

    if (parentId) {
      return {
        parentActivityId: parentId,
        grandparentActivityId: grandParentId ?? null,
      };
    }

    return {
      parentActivityId: idCourse || idPath || null,
      grandparentActivityId: idCourse && idPath ? idPath : null,
    };
  };

  const getChangeStatusPath = (row: any, section: string) => {
    const { parentActivityId, grandparentActivityId } = getHierarchyIds(row);

    return `${row?.userId}/${idLO}/${row?.learningObject?.idLearningObject}/${parentActivityId ?? "null"}/${grandparentActivityId ?? "null"}/${section}`;
  };

  const { trigger: triggerIsMultiScorm } = useSWRMutation(
    ServicesURL.scorm + `/is-multiscorm`,
    (url: string, { arg }: { arg: any }) => {
      const { parentActivityId, grandparentActivityId } = getHierarchyIds(arg);

      return fetcher<any>(
        `${url}?corporateId=${id}&studentId=${arg?.userId}&activityId=${idLO}&learningObjectId=${arg?.learningObject?.idLearningObject}&parentActivityId=${parentActivityId ?? ""}&grandparentActivityId=${grandparentActivityId ?? ""}`,
        MethodType.GET,
      );
    },
    { onError: () => null },
  );

  const isMultiscorm = async (row: any, index: any) => {
    let result = await triggerIsMultiScorm(row);
    if (result) {
      navigate(getChangeStatusPath(row, "multi-scorm"));
    } else {
      navigate(getChangeStatusPath(row, "change-status"));
    }
  };

  // Esegue il flusso di ripianifica edizione
  const proceedReschedule = useCallback(
    async (row: any) => {
      try {
        const corporateId = id || "1";
        const objectId = row.learningObject?.objectId ?? row.objectId;
        const studentEid = row.idUser;

        // Recupero le edizioni disponibili
        const response = await triggerGetEnrollableEditions({
          corporateId: String(corporateId),
          objectId: String(objectId),
          studentEid: String(studentEid),
        });

        if (response?.editions && response?.editions?.length > 0) {
          setSelectedEditionToReschedule(row);
          setEnrollableEditionsWithSessions(response?.editions);
          // Se lo status dell'iscrizione è "P" (IN PROGRESS) mostro un warning prima di aprire la modal di cambio edizione
          if (row?.status === "P") {
            setInProgressWarning({ type: "reschedule", row });
            return;
          }
          setOpenModalEditionChange(true);
        } else {
          setOpenModalEditionChangeToast({
            s: StatusEnum.CUSTOM,
            message: "",
            customContent: (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: "1rem",
                  mb: "1.5rem",
                }}
              >
                <Warning sx={{ fontSize: "60px", color: "orange" }} />
                <Typography variant="h5" component={"p"}>
                  {t("change-edition.no_edition_available_for_change", {
                    ns: "modal",
                  })}
                </Typography>
              </Box>
            ),
          });
        }
      } catch (error) {
        const message = onErrorMessage(error);
        setOpenModalEditionChangeToast({ s: StatusEnum.KO, message });
        console.error(error);
      }
    },
    [id, t, triggerGetEnrollableEditions],
  );

  // Esegue il flusso di disiscrizione singola
  const proceedDeleteSingleEnroll = useCallback((row: any) => {
    setEnrollDeleted(row);
    setGroupIDWarning(row?.massiveGroupId);
  }, []);

  const configurationActionForRow: ActionsType[] | undefined =
    React.useMemo(() => {
      let actionsArray = actionForRow;
      const isSon =
        router.pathname.includes("overall-catalog") &&
        router.pathname.includes("associated-");

      if (isSon) {
        const hideIconsKey = [
          "single_enroll",
          "delete_single_enroll",
          "group_enroll",
        ];
        const hideIconsList = actionForRow?.filter((action: any) => {
          return !hideIconsKey.includes(action.key);
        });
        actionsArray = hideIconsList;
      }

      return actionsArray?.map((action: any) => {
        if (action.key === "reschedule") {
          return {
            ...action,
            callBack: async (row: any) => {
              await proceedReschedule(row);
            },
          };
        }
        if (action.key === "change-status") {
          return {
            ...action,
            callBack: (row: any, index: any) => {
              if (activityType === "DA_SCORM") {
                isMultiscorm(row, index);
              } else {
                navigate(getChangeStatusPath(row, "change-status"));
              }
            },
          };
        }
        if (action.key === "refresh_certificate_data") {
          return {
            ...action,
            callBack: (row: any) => setCertificateDataToRefresh(row),
            getIcon: () => <AutorenewIcon />,
          };
        }
        if (action.type === "button" && action.icon === "download") {
          return {
            ...action,
            callBack: async (row: any) => {
              const learnerId = row.userId;
              const objectId = row.learningObject?.objectId ?? row.objectId;

              const certificateIds: number[] = Array.isArray(row.certificateIds)
                ? row.certificateIds
                : [];

              if (!objectId || !learnerId || certificateIds.length === 0)
                return;

              try {
                setOpenToast({ s: StatusEnum.LOADING, message: "loading" });

                if (certificateIds.length === 1) {
                  const issuedCertificateId = certificateIds[0];

                  const fileName = `issued_certificate_lo_${objectId}_learner_${learnerId}.pdf`;

                  const url = `${ServicesURL.getIssuedCertificate.replace(":certificateId", String(issuedCertificateId))}?objectId=${objectId}&learnerId=${learnerId}`;

                  await downloadBlobFile(url, null, fileName, MethodType.GET);
                } else {
                  const issuedCertificates = certificateIds.map(
                    (issuedCertificateId) => ({
                      learnerId,
                      objectId,
                      issuedCertificateId,
                    }),
                  );

                  const fileName = `issued_certificates_lo_${objectId}_learner_${learnerId}.zip`;

                  await downloadBlobFile(
                    `${ServicesURL.getIssuedCertificatesZip}`,
                    {
                      issuedCertificates: issuedCertificates,
                    },
                    fileName,
                  );
                }

                setOpenToast({ s: StatusEnum.OK, message: "" });
              } catch (error) {
                const message = onErrorMessage(error);
                setOpenToast({ s: StatusEnum.KO, message: message });
                console.error(error);
              }
            },
            getIcon: (row: any) => <DownloadIcon />,
          };
        } else {
          if (action.type === "button" && action.icon !== "delete") {
            if (idEdition) return {};
            return {
              ...action,
              callBack: (row: any) => {
                if (
                  row?.learningObject?.blendedParentId &&
                  row?.learningObject?.blendedParentId !== Number(idLO) &&
                  action.key !== "enroll_history"
                ) {
                  setEditDescriptionErrorModal(true);
                  setTitleError("Modifica Iscrizione");
                  setDescriptionError(
                    `L'utente è stato iscritto all'oggetto formativo tramite Percorso/Corso ID "${row.learningObject.blendedParentId}". Per poter modificare l'iscrizione dell'utente all'oggetto formativo, è necessario modificare l'iscrizione sul Percorso/Corso ID "${row.learningObject.blendedParentId}"`,
                  );
                  return;
                }

                const isAfterStartEdition = dayjs().isAfter(
                  dayjs(row["editionStartDate"]),
                );
                const isBeforeEndEdition = dayjs().isBefore(
                  dayjs(row["editionEndDate"]),
                );

                const isEditionInProgress =
                  isAfterStartEdition && isBeforeEndEdition;

                if (
                  !!row?.editionStartDate &&
                  isEditionInProgress &&
                  action.key !== "enroll_history"
                ) {
                  setErrorContext({
                    title: "Warning",
                    content: `${t("warning-startEdition-enroll", {
                      ns: "modal",
                    })}`,
                  });
                  setOpenErrorContext(true);
                  return;
                }

                if (action.key === "group_enroll") {
                  setEnrollGroupEdit(row);
                  setGroupIDWarning(row?.massiveGroupId);
                } else if (action.key === "single_enroll") {
                  navigate(`${row.userId}/enrollment`);
                  // if (row?.blended) {
                  //   // setEnrollSingleEdit(row)
                  //   setErrorContext({
                  //     title: "Warning",
                  //     content: `${t("warning-blended-enroll", {
                  //       ns: "modal",
                  //     })}`,
                  //   });
                  //   setOpenErrorContext(true);
                  // } else {
                  //   navigate(`${row.userId}/enrollment`);
                  // }
                } else if (action.key === "enroll_history") {
                  navigate(`${row.userId}/enrollment-history`);
                }
              },
            };
          }
          if (action.type === "button" && action.icon === "delete") {
            if (
              action.key === "single_enroll" ||
              action.key === "delete_single_enroll"
            ) {
              return {
                ...action,
                callBack: (row: any) => {
                  if (
                    !!row?.learningObject?.blendedParentId &&
                    row?.learningObject?.blendedParentId !== Number(idLO)
                  ) {
                    setEditDescriptionErrorModal(true);
                    setTitleError("Elimina Iscrizione");
                    setDescriptionError(
                      `L'utente è stato iscritto all'oggetto formativo tramite Percorso/Corso ID "${row.learningObject.blendedParentId}". Per poter eliminare l'iscrizione dell'utente all'oggetto formativo, è necessario eliminare l'iscrizione sul Percorso/Corso ID "${row.learningObject.blendedParentId}"`,
                    );
                    return;
                  }
                  // Se lo status dell'iscrizione è "P" (IN PROGRESS) mostro un warning prima di procedere con la disiscrizione
                  if (row?.status === "P") {
                    setInProgressWarning({ type: "delete", row });
                    return;
                  }
                  proceedDeleteSingleEnroll(row);
                  // if (row?.blended) {
                  //   setErrorContext({
                  //     title: "Warning",
                  //     content: `${t("warning-blended-enroll", {
                  //       ns: "modal",
                  //     })}`,
                  //   });
                  //   setOpenErrorContext(true);
                  // } else {
                  //   setEnrollDeleted(row);
                  //   setGroupIDWarning(row?.massiveGroupId);
                  // }
                },
                labelTranslate: (action.label as any)[i18n.language],
              };
            } else {
              if (idEdition) return {};
              return {
                ...action,
                callBack: (row: any) => {
                  if (
                    !!row?.learningObject?.blendedParentId &&
                    row?.learningObject?.blendedParentId !== Number(idLO)
                  ) {
                    setEditDescriptionErrorModal(true);
                    setTitleError("Elimina Iscrizione");
                    setDescriptionError(
                      `L'utente è stato iscritto all'oggetto formativo tramite Percorso/Corso ID "${row.learningObject.blendedParentId}". Per poter eliminare l'iscrizione dell'utente all'oggetto formativo, è necessario eliminare l'iscrizione sul Percorso/Corso ID "${row.learningObject.blendedParentId}"`,
                    );
                    return;
                  }
                  setEnrollGroupDeleted(row);
                  setGroupIDWarning(row?.massiveGroupId);
                },
                labelTranslate: (action.label as any)[i18n.language],
              };
            }
          }
        }
        return {
          ...action,
          labelTranslate: (action.label as any)[i18n.language],
        };
      });
    }, [actionForRow, i18n.language]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    configRowNew.forEach((configRow: any) => {
      let fixedDisable = false;
      if (!idEdition && configRow.key === "group_enroll" && !fixedDisable) {
        if (row.original?.massiveGroupId) {
          configRow.disabled = false;
        } else {
          configRow.disabled = true;
          fixedDisable = true;
        }
      }

      if (
        configRow.key === "reschedule" ||
        configRow.key === "delete_single_enroll" ||
        (configRow.key === "single_enroll" && configRow.icon === "delete")
      ) {
        const hasUserCompletedEdition = row?.original?.status === "C";
        configRow.disabled = hasUserCompletedEdition;
      }

      if (configRow.key === "download_certificate") {
        const hasDownloadCertificatePermission = user?.permissions.find(
          (userPermission) => userPermission === configRow.permission,
        );

        // configRow.disabled =
        //   !row.original?.certificateId || !hasDownloadCertificatePermission;
        const ids = row.original?.certificateIds;
        configRow.disabled =
          !Array.isArray(ids) ||
          ids.length === 0 ||
          !hasDownloadCertificatePermission;
      }

      // attiva solo se l'attestato è già stato generato per quell'utente
      if (configRow.key === "refresh_certificate_data") {
        const hasRefreshCertificatePermission = user?.permissions.find(
          (userPermission) => userPermission === configRow.permission,
        );
        const ids = row.original?.certificateIds;
        configRow.disabled =
          !Array.isArray(ids) ||
          ids.length === 0 ||
          !hasRefreshCertificatePermission;
      }

      if (configRow.key === "change-status") {
        let activitiesCategories = [
          "DA_XAPI",
          "DA_SCORM",
          "DA_EXTERNAL_RES",
          "DA_YOUTUBE",
          "DA_LINKEDIN",
          "DA_LTI",
          "DA_SURVEY",
          "DA_URL_RES",
        ];

        if (
          activitiesCategories.includes(`${activityType}`) &&
          row.original?.status === "E"
        ) {
          configRow.disabled = true;
        } else {
          configRow.disabled = false;
        }
      }
    });

    let activitiesCategories = [
      "DA_XAPI",
      "DA_SCORM",
      "DA_PHYSICAL_CLASS",
      "DA_VIRTUAL_CLASS",
      "DA_EXTERNAL_RES",
      "DA_YOUTUBE",
      "DA_LINKEDIN",
      "DA_LTI",
      "DA_SURVEY",
      "DA_URL_RES",
    ];

    if (!activitiesCategories.includes(`${activityType}`)) {
      configRowNew = configRowNew.filter(
        (configRow: any) => configRow.key !== "change-status",
      );
    }

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const configurationTopToolbar: ActionsType[] | undefined =
    React.useMemo(() => {
      return (actionsTopToolbar || []).map((action: any) => {
        if (action.key === "refresh_certificate_data_massive") {
          const hasRefreshCertificatePermission = user?.permissions.find(
            (userPermission) => userPermission === action.permission,
          );
          if (!hasRefreshCertificatePermission) {
            return {};
          }
          return {
            ...action,
            labelTranslate: (action.label as any)[i18n.language],
            // stesso segnale del download massivo: se nessuno ha conseguito
            // l'attestato non c'è nulla da riallineare
            callBack: certificatesAvailable
              ? () => setOpenModalRefreshCertificateMassive(true)
              : null,
            getIcon: () => <AutorenewIcon />,
          };
        }
        if (action.type === "button" && action.icon === "download") {
          const hasDownloadCertificatePermission = user?.permissions.find(
            (userPermission) => userPermission === action.permission,
          );
          if (!hasDownloadCertificatePermission) {
            return {};
          }
          return {
            ...action,
            labelTranslate: (action.label as any)[i18n.language],
            callBack: certificatesAvailable
              ? async (el: any) => {
                  try {
                    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });

                    const fileName = `certificates_${buildDateStr(new Date())}.zip`;

                    const { enrollmentResponses } = dataTable;
                    const issuedCertificates = (
                      enrollmentResponses ?? []
                    ).flatMap((row: any) => {
                      const learnerId = row.userId;
                      const objectId =
                        row.learningObject?.objectId ?? row.objectId;

                      const certificateIds: number[] =
                        Array.isArray(row.certificateIds) &&
                        row.certificateIds.length > 0
                          ? row.certificateIds
                          : row.certificateId
                            ? [row.certificateId]
                            : [];

                      if (
                        !learnerId ||
                        !objectId ||
                        certificateIds.length === 0
                      )
                        return [];

                      return certificateIds.map((issuedCertificateId) => ({
                        learnerId,
                        objectId,
                        issuedCertificateId,
                      }));
                    });

                    if (issuedCertificates.length === 0) {
                      setOpenToast({
                        s: StatusEnum.KO,
                        message:
                          "Nessun certificato disponibile per il download",
                      });
                      return;
                    }

                    await downloadBlobFile(
                      `${ServicesURL.getIssuedCertificatesZip}`,
                      {
                        issuedCertificates: issuedCertificates,
                      },
                      fileName,
                    );
                    setOpenToast({ s: StatusEnum.OK, message: "" });
                  } catch (error) {
                    const message = onErrorMessage(error);
                    setOpenToast({ s: StatusEnum.KO, message: message });
                    console.error(error);
                  }
                }
              : null,
            getIcon: () => <DownloadIcon />,
          };
        }
        return {
          ...action,
          labelTranslate: (action.label as any)[i18n.language],
        };
      });
    }, [actionsTopToolbar, i18n.language]);

  const handleDelete = React.useCallback(
    async (rowArg?: any) => {
      const target = rowArg ?? enrollDeleted;
      if (target) {
        setOpenToastDeleted({ s: StatusEnum.LOADING, message: "loading" });
        let newData = [];
        try {
          if (idEdition) {
            await triggerDeleteEdition({
              studentEid: target.idUser.toString(),
              objectId: target.objectId.toString(),
            });
            newData = [
              ...dataTable.users.filter(
                (el: any) => el.idUser !== target.idUser,
              ),
            ];
          } else {
            await triggerDelete({
              enrollmentEId: target.enrollId.toString(),
            });
            newData = [
              ...dataTable.enrollmentResponses.filter(
                (el: any) => el.id !== target.enrollId,
              ),
            ];
          }

          mutate &&
            mutate({ ...dataTable, ...{ enrollmentResponses: newData } });
          setOpenToastDeleted({ s: StatusEnum.OK, message: "ok" });
        } catch (e) {
          console.error(e);
          setEnrollDeleted(undefined);
          setOpenToastDeleted(undefined);
        }
      }
    },
    [dataTable, enrollDeleted, mutate, triggerDelete],
  );

  const subscribedObjectId = useMemo(() => {
    if (activityType) {
      return isOverallCatalog ? idLO : idPath || idCourse || idLO;
    }
    return idLO;
  }, [activityType, isOverallCatalog, idLO, idPath, idCourse]);

  const handleRefreshCertificateDataMassive = React.useCallback(async () => {
    setOpenToastRefreshCertificate({
      s: StatusEnum.LOADING,
      message: "loading",
    });

    try {
      await triggerRefreshCertificateDataMassive({
        corporateId: Number(id),
        objectId: Number(subscribedObjectId),
      });

      // da qui in poi l'avanzamento si segue sul banner, che il tracciamento
      // aggiorna da solo fino alla fine dell'elaborazione
      await trackRefreshProcess();

      setOpenToastRefreshCertificate({
        s: StatusEnum.OK,
        message: t("refresh-certificate-data.massive-started", { ns: "modal" }),
      });
    } catch (error) {
      const message = onErrorMessage(error);
      setOpenToastRefreshCertificate({ s: StatusEnum.KO, message });
      console.error(error);
    }
  }, [
    id,
    subscribedObjectId,
    t,
    trackRefreshProcess,
    triggerRefreshCertificateDataMassive,
  ]);

  const handleRefreshCertificateData = React.useCallback(async () => {
    if (!certificateDataToRefresh) return;

    setOpenToastRefreshCertificate({
      s: StatusEnum.LOADING,
      message: "loading",
    });

    const learnerId = certificateDataToRefresh.userId;
    const objectId =
      certificateDataToRefresh.learningObject?.objectId ??
      certificateDataToRefresh.objectId;
    const certificateIds: number[] = Array.isArray(
      certificateDataToRefresh.certificateIds,
    )
      ? certificateDataToRefresh.certificateIds
      : [];

    try {
      const response = await triggerRefreshCertificateData({
        corporateId: Number(id),
        objectId: Number(objectId),
        learnerId: Number(learnerId),
        issuedCertificateIds: certificateIds,
      });

      const skipped = response?.skipped?.length || 0;
      const refreshed = response?.refreshed?.length || 0;

      // gli attestati con firma digitale non sono aggiornabili: se non ne resta
      // nessun altro va detto, altrimenti l'utente crede sia andato tutto a buon fine
      if (refreshed === 0 && skipped > 0) {
        setOpenToastRefreshCertificate({
          s: StatusEnum.KO,
          message: t("refresh-certificate-data.all-skipped", { ns: "modal" }),
        });
        return;
      }

      mutate && mutate();
      setOpenToastRefreshCertificate({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      const message = onErrorMessage(error);
      setOpenToastRefreshCertificate({ s: StatusEnum.KO, message });
      console.error(error);
    }
  }, [certificateDataToRefresh, id, mutate, t, triggerRefreshCertificateData]);

  const handleDeleteGroup = React.useCallback(async () => {
    if (enrollGroupDeleted) {
      setOpenToastDeleted({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDeleteGroups({
          groupId: enrollGroupDeleted?.massiveGroupId,
          groupType: enrollGroupDeleted?.groupType,
        });
        const newData = [
          ...dataTable.enrollmentResponses.filter(
            (el: any) =>
              el.massiveGroupId !== enrollGroupDeleted.massiveGroupId,
          ),
        ];
        mutate && mutate({ ...dataTable, ...{ enrollmentResponses: newData } });
        setOpenToastDeleted({ s: StatusEnum.OK, message: "ok" });
      } catch (e) {
        console.error(e);
        setOpenToastDeleted(undefined);
        setEnrollGroupDeleted(undefined);
      }
    }
  }, [dataTable, enrollGroupDeleted, mutate, triggerDelete]);

  const getEnrollmentResponse = () => {
    const newList = dataTable?.enrollmentResponses.map((item: any) => {
      let newItem = {
        ...item,
        grandParentId: item.learningObject.grandParentId,
        parentId: item.learningObject.parentId,
      };
      if (
        !!item.learningObject.blendedParentId &&
        item.learningObject.blendedParentId !== Number(idLO)
      ) {
        return {
          ...newItem,
          blendedParentId: item.learningObject.blendedParentId,
        };
      }
      return { ...newItem };
    });

    return newList || [];
  };
  const downloadEnrolled = async () => {
    let filteredQueryParams = "";
    if (!filters[filterId]) {
      filteredQueryParams = `&pageIndex=0&pageSize=20`;
    } else {
      for (const key in filters[filterId]) {
        filteredQueryParams =
          filteredQueryParams + `&${key}=${filters[filterId][key]}`;
      }
    }

    try {
      let response = await triggerDownloadEnrolled(
        `?editionId=${idEdition}&corporateId=${id || "1"}${filteredQueryParams}`,
      );
      let fileName = `users_subscribed_page n^${(filters[filterId] ? filters[filterId].pageIndex : 0) + 1}.csv`;
      const blob = new Blob([response], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error(e);
    }
  };

  const [columnsMerged, setColumnsMerged] = useState<ColumnsType[]>();

  useEffect(() => {
    if (!columnsMerged) {
      if (columns) {
        setColumnsMerged([
          ...(columns.map((el: ColumnsType) => {
            switch (el.accessorKey) {
              case "massiveGroupId": {
                return {
                  ...el,
                  hasFilter:
                    location && location?.state && location?.state?.setGroup
                      ? false
                      : true,
                };
              }
              default:
                return el;
            }
          }) || []),
        ]);
      }
    }
  }, [columns, columnsMerged]);

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-users.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${location.state?.row?.title || dataDetail?.name} - ${t(
            "subscribed-users.hero-title",
          )}`}
          description={t("subscribed-users.hero-description")}
        />
        <ButtonBackPage customRedirect={-1} />
        {columns ? (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <Table
              id={idEdition ? PagesID["enrollment.subscribed.edition"] : page}
              data={
                idEdition
                  ? dataTable?.users || []
                  : getEnrollmentResponse() || []
              }
              columns={
                columnsMerged
                  ? translateHeaders(columnsMerged, i18n.language)
                  : []
              }
              configurationTopToolbar={configurationTopToolbar.map(
                (action: any) => {
                  if (action.accessorKey === "download_enrolled") {
                    // TODO: Rimuovere questo workaround quando i coni di visibilità saranno fixati
                    const isPrivilegedRole =
                      user?.roles?.includes(RolesDigitedEnum.PRODUCT_OWNER) ||
                      user?.roles?.includes(RolesDigitedEnum.BUSINESS_ADMIN);
                    const isDownloadDisabledForHR =
                      process.env.REACT_APP_DISABLE_DOWNLOAD_USERS_HR ===
                        "true" &&
                      user?.roles?.includes(RolesDigitedEnum.HR) &&
                      !isPrivilegedRole;

                    return {
                      ...action,
                      callBack:
                        !isDownloadDisabledForHR &&
                        idEdition &&
                        dataTable?.users &&
                        dataTable?.users.length > 0
                          ? () => downloadEnrolled()
                          : null,
                    };
                  } else {
                    return action;
                  }
                },
              )}
              configurationActionForRow={configurationActionForRow}
              renderRowActions={customRenderRowActions}
              totalPages={dataTable?.metadata?.totalElements}
              modalTitle={t("hero-title")?.replace("-", "") || ""}
              testIdPrefix="subscribed-users-list"
              renderBelowFilters={
                currentRefreshProcess ? (
                  <AsyncProcessBanner
                    process={currentRefreshProcess}
                    progressPercentage={refreshProgressPercentage}
                    onDownloadDiscards={
                      currentRefreshProcess.status ===
                      AsyncProcessStatusEnum.COMPLETED_WITH_ERRORS
                        ? () =>
                            downloadRefreshDiscardsCsv(
                              currentRefreshProcess.processUuid,
                            )
                        : undefined
                    }
                    onDismiss={() => {
                      setRefreshOutcome(null);
                      dismissRefreshProcess();
                    }}
                    variant="certificates"
                    completedTitle={
                      refreshOutcome
                        ? String(
                            t(
                              `card.refresh-certificates.${getRefreshResultTitleKey(refreshOutcome)}`,
                              { ns: "users-and-groups" },
                            ),
                          )
                        : undefined
                    }
                    completedLines={
                      refreshOutcome
                        ? getRefreshResultLines(refreshOutcome).map(
                            (line, index, lines) => (
                              <>
                                <Trans
                                  i18nKey={`card.refresh-certificates.${line.key}`}
                                  ns="users-and-groups"
                                  count={line.count}
                                  components={{ strong: <strong /> }}
                                />
                                {index < lines.length - 1 ? ";" : "."}
                              </>
                            ),
                          )
                        : undefined
                    }
                  />
                ) : undefined
              }
            />
          </Box>
        ) : (
          <FormLoadingStateCst />
        )}
      </Container>
      <ModalConfirmsCst
        open={!!enrollGroupEdit}
        title={t("edit-item", { ns: "modal" })}
        description={
          groupIDWarning && !idEdition
            ? t("warning-edit-group-enroll", { ns: "modal" }).replace(
                ":ID",
                groupIDWarning,
              )
            : t("confirm-edit", { ns: "modal" })
        }
        routeToSamePage={true}
        onCallBackClosed={() => {
          setEnrollGroupEdit(undefined);
          setOpenToastDeleted(undefined);
        }}
        onActionConfirmed={() =>
          enrollGroupEdit &&
          navigate(
            `${enrollGroupEdit.groupType}/${enrollGroupEdit.massiveGroupId}/enrollment`,
          )
        }
        status={openToastDeleted}
      />
      <ModalConfirmsCst
        open={!!enrollDeleted}
        title={t("delete-item", { ns: "modal" })}
        description={
          groupIDWarning && !idEdition
            ? t("warning-delete-single-enroll", { ns: "modal" }).replace(
                ":ID",
                groupIDWarning,
              )
            : t("confirm-delete", { ns: "modal" })
        }
        routeToSamePage={true}
        onCallBackClosed={() => {
          setEnrollDeleted(undefined);
          setOpenToastDeleted(undefined);
        }}
        onActionConfirmed={() => enrollDeleted && handleDelete()}
        status={openToastDeleted}
      />
      <ModalConfirmsCst
        open={openModalRefreshCertificateMassive}
        title={t("refresh-certificate-data.massive-title", { ns: "modal" })}
        description={t("refresh-certificate-data.massive-description", {
          ns: "modal",
        })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenModalRefreshCertificateMassive(false);
          setOpenToastRefreshCertificate(undefined);
        }}
        onActionConfirmed={() => handleRefreshCertificateDataMassive()}
        status={openToastRefreshCertificate}
      />
      <ModalConfirmsCst
        open={!!certificateDataToRefresh}
        title={t("refresh-certificate-data.title", { ns: "modal" })}
        description={t("refresh-certificate-data.description", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setCertificateDataToRefresh(undefined);
          setOpenToastRefreshCertificate(undefined);
        }}
        onActionConfirmed={() => handleRefreshCertificateData()}
        status={openToastRefreshCertificate}
      />
      <ModalConfirmsCst
        open={!!enrollGroupDeleted}
        title={t("delete-item", { ns: "modal" })}
        description={
          groupIDWarning && !idEdition
            ? t("warning-delete-group-enroll", { ns: "modal" }).replace(
                ":ID",
                groupIDWarning,
              )
            : t("confirm-delete", { ns: "modal" })
        }
        routeToSamePage={true}
        onCallBackClosed={() => {
          setEnrollGroupDeleted(undefined);
          setOpenToastDeleted(undefined);
        }}
        onActionConfirmed={() => enrollGroupDeleted && handleDeleteGroup()}
        status={openToastDeleted}
      />
      <ModalConfirmsCst
        open={editDescriptionErrorModal}
        title={titleError}
        description={descriptionError}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setEditDescriptionErrorModal(false);
          setTitleError("");
          setDescriptionError("");
        }}
        removeButtons={true}
      />
      <ModalConfirmsCst
        open={!!openToast}
        title={t("download-certificates.title", { ns: "modal" })}
        description={""}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        removeButtons={true}
        status={openToast}
      />
      <ModalConfirmsCst
        open={!!openModalEditionChangeToast}
        title={""}
        description={""}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenModalEditionChangeToast(undefined);
          if (
            !isPollingEditionChange &&
            enrollableEditionsWithSessions.length !== 0
          )
            navigate(-1);
        }}
        status={openModalEditionChangeToast}
      />
      <ModalConfirmsCst
        open={!!inProgressWarning}
        title={t("warning-title", { ns: "modal" })}
        description={""}
        routeToSamePage={true}
        onCallBackClosed={() => {
          if (inProgressWarning?.type === "reschedule") {
            setSelectedEditionToReschedule(null);
            setEnrollableEditionsWithSessions([]);
          }
          setInProgressWarning(null);
        }}
        onActionConfirmed={() => {
          const pending = inProgressWarning;
          setInProgressWarning(null);
          if (!pending) return;
          if (pending.type === "delete") {
            setEnrollDeleted(pending.row);
            setGroupIDWarning(pending.row?.massiveGroupId);
            handleDelete(pending.row);
          } else {
            setOpenModalEditionChange(true);
          }
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "1rem",
            mb: "1rem",
          }}
        >
          <Warning sx={{ fontSize: "60px", color: "orange" }} />
          <Typography variant="h6" component={"p"} sx={{ textAlign: "center" }}>
            {t("warning-edition-in-progress", { ns: "modal" })}
          </Typography>
        </Box>
      </ModalConfirmsCst>
      {openModalEditionChange && enrollableEditionsWithSessions ? (
        <ModalEditionChange
          open={openModalEditionChange}
          handleClose={() => {
            setOpenModalEditionChange(false);
            setEnrollableEditionsWithSessions([]);
          }}
          enrollableEditionsWithSessions={enrollableEditionsWithSessions}
          onEditionChange={async (newEditionId) => {
            if (!selectedEditionToReschedule) return;
            try {
              const objectId =
                selectedEditionToReschedule.learningObject?.objectId ??
                selectedEditionToReschedule.objectId;

              await triggerPatchEnrollment([
                {
                  corporateId: id ? +id : undefined,
                  editionId: newEditionId,
                  objectId: objectId ? +objectId : undefined,
                  isToj: false,
                  userId: [selectedEditionToReschedule.idUser],
                  mandatory: null,
                  isAdmin: true,
                },
              ]);

              setOpenModalEditionChange(false);
              setEnrollableEditionsWithSessions([]);

              setOpenModalEditionChangeToast({
                s: StatusEnum.OK,
                message: t("change-edition.enrollment_edition_success", {
                  ns: "modal",
                }),
              });

              setNewEditionIdForPolling(newEditionId);
              pollingCompletedRef.current = false;
              setIsPollingEditionChange(true);
            } catch (error: any) {
              setOpenModalEditionChange(false);
              setEnrollableEditionsWithSessions([]);

              setOpenModalEditionChangeToast({
                s: StatusEnum.KO,
                message:
                  formatSessionOccupiedError(error, t) ||
                  t("change-edition.enrollment_edition_error", { ns: "modal" }),
              });
              mutate();
            }
          }}
          isVirtual={isVirtualEditionSubscribedPage}
          isLoading={isMutatingPatchEnrollment}
          isPollingEditionChange={isPollingEditionChange}
        />
      ) : null}
    </AnimationFadeIn>
  );
};

export default SubscribedUsers;
